import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { ReceivingLineStatusBadge } from './ReceivingLineStatusBadge';
import { QCStatusBadge } from './QCStatusBadge';
import type { ReceivingLine, ReceivingOrderStatus } from '../../types';

interface ReceivingLinesTableProps {
  lines: ReceivingLine[];
  orderStatus: ReceivingOrderStatus;
  onReceive?: (line: ReceivingLine) => void;
  onUpdateQC?: (line: ReceivingLine) => void;
  onRemove?: (line: ReceivingLine) => void;
}

export function ReceivingLinesTable({
  lines,
  orderStatus,
  onReceive,
  onUpdateQC,
  onRemove,
}: ReceivingLinesTableProps) {
  if (lines.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No lines in this receiving order
      </div>
    );
  }

  const canReceive = orderStatus === 'in_progress';
  const canUpdateQC = orderStatus === 'in_progress' || orderStatus === 'pending_qc';
  const canRemove = orderStatus === 'draft';

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>SKU</TableHead>
          <TableHead>Description</TableHead>
          <TableHead className="text-right">Expected</TableHead>
          <TableHead className="text-right">Received</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>QC Status</TableHead>
          <TableHead>Location</TableHead>
          <TableHead className="w-[150px]">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {lines.map((line) => (
          <TableRow key={line.id}>
            <TableCell className="font-mono font-medium">
              {line.sku || line.sku_code || '—'}
            </TableCell>
            <TableCell>
              {line.description || line.sku_name || '—'}
            </TableCell>
            <TableCell className="text-right">
              {line.expected_quantity}
              {line.unit_of_measure && ` ${line.unit_of_measure}`}
            </TableCell>
            <TableCell className="text-right">
              {line.received_quantity}
              {line.unit_of_measure && ` ${line.unit_of_measure}`}
            </TableCell>
            <TableCell>
              <ReceivingLineStatusBadge status={line.line_status} />
            </TableCell>
            <TableCell>
              {line.qc_status && <QCStatusBadge status={line.qc_status} />}
            </TableCell>
            <TableCell className="text-sm">
              {line.storage_location || line.actual_location_code || '—'}
            </TableCell>
            <TableCell>
              <div className="flex gap-1">
                {canReceive && line.line_status !== 'complete' && onReceive && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onReceive(line)}
                  >
                    Receive
                  </Button>
                )}
                {canUpdateQC && line.line_status !== 'pending' && line.qc_status !== 'passed' && onUpdateQC && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onUpdateQC(line)}
                  >
                    QC
                  </Button>
                )}
                {canRemove && onRemove && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600"
                    onClick={() => onRemove(line)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
